import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { adminApi } from '../../../api/adminApi';
import CopyButton from '../common/CopyButton';
import './WalletsPanel.css';

export default function WalletsPanel() {
    const [showPrivateKeys, setShowPrivateKeys] = useState<Record<string, boolean>>({});

    const { data: wallets, isLoading } = useQuery({
        queryKey: ['wallets'],
        queryFn: adminApi.getWallets,
        refetchInterval: 30000,
    });

    const togglePrivateKey = (address: string) => {
        setShowPrivateKeys(prev => ({
            ...prev,
            [address]: !prev[address]
        }));
    };

    if (isLoading) {
        return <div className="loading">Loading wallets...</div>;
    }

    // Mock data if API returns empty (for development)
    const displayWallets = wallets?.length ? wallets : [
        {
            network: 'TRON',
            address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
            balance: 50000.00,
            nativeBalance: 1500.00,
            nativeSymbol: 'TRX',
            status: 'ACTIVE'
        },
        {
            network: 'ETHEREUM',
            address: '0xdac17f958d2ee523a2206206994597c13d831ec7',
            balance: 25000.00,
            nativeBalance: 2.5,
            nativeSymbol: 'ETH',
            status: 'ACTIVE'
        },
        {
            network: 'SOLANA',
            address: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
            balance: 10000.00,
            nativeBalance: 15.0,
            nativeSymbol: 'SOL',
            status: 'INACTIVE'
        }
    ];

    return (
        <div className="wallets-panel">
            <div className="wallets-header">
                <div className="total-balance glass">
                    <h3>Total Treasury Balance</h3>
                    <div className="balance-amount">$85,000.00</div>
                </div>
                <button className="generate-btn glass">
                    <span>+</span> Generate New Wallet
                </button>
            </div>

            <div className="wallets-grid">
                {displayWallets.map((wallet: any, index: number) => (
                    <motion.div
                        key={wallet.address}
                        className={`wallet-card glass ${wallet.network.toLowerCase()}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                    >
                        <div className="wallet-header">
                            <div className="network-badge">
                                <img
                                    src={`/icons/${wallet.network.toLowerCase()}.svg`}
                                    alt={wallet.network}
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                />
                                {wallet.network}
                            </div>
                            <span className={`status-dot ${wallet.status.toLowerCase()}`}></span>
                        </div>

                        <div className="wallet-body">
                            <div className="balance-group">
                                <label>USDT Balance</label>
                                <div className="main-balance">${wallet.balance.toLocaleString()}</div>
                            </div>

                            <div className="balance-group">
                                <label>Gas Balance</label>
                                <div className="sub-balance">
                                    {wallet.nativeBalance} {wallet.nativeSymbol}
                                </div>
                            </div>

                            <div className="address-group">
                                <label>Address</label>
                                <div className="address-box">
                                    <span className="truncate">{wallet.address}</span>
                                    <CopyButton text={wallet.address} />
                                </div>
                            </div>

                            <div className="keys-group">
                                <label>Private Key</label>
                                <div className="key-box">
                                    <span className="key-text">
                                        {showPrivateKeys[wallet.address]
                                            ? '5HpHagT65TZzG1PH3CSu63k8DbpvD8s5ip4nEB3kEsreAnchuDf' // Mock key
                                            : '••••••••••••••••••••••••••••••••'}
                                    </span>
                                    <button
                                        className="toggle-key-btn"
                                        onClick={() => togglePrivateKey(wallet.address)}
                                    >
                                        {showPrivateKeys[wallet.address] ? 'Hide' : 'Show'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="wallet-footer">
                            <button className="action-btn">View on Explorer</button>
                            <button className="action-btn danger">Deactivate</button>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
